import { useEffect, useState } from "react";
import Modal from "../Modal/Modal";
import Titulo from "../Modal/ModaisConfigsPrimeiroAcesso/Titulo/Titulo";
import { Button } from "../Button";
import { Input } from "../Input";
import { Toggle } from "../Toggle/Toggle";
import { useTheme } from "../../contexts/Theme/ThemeContext";
import { tiposVariacaoApi } from "../../hooks/tiposVariacaoApi";
import { TipoVariacao } from "../../types/Produto";
import { ContainerModalTiposVariacao, CriarTipoContainer, Botoes } from "./styles";
import { ReactComponent as Voltar } from "../../assets/newImages/icons/angle-down.svg";
import Swal from "sweetalert2";
import Table from "../Table/Table";

interface ModalTiposVariacaoProps {
  isOpen: boolean;
  setModal: (value: boolean) => void;
  onSave?: (tipos: TipoVariacao[]) => void;
}

const tiposPadrao = ["Tamanho", "Cor/banho", "Gênero"];

export const ModalTiposVariacao: React.FC<ModalTiposVariacaoProps> = ({
  isOpen,
  setModal,
  onSave,
}) => {
  const { theme } = useTheme();
  const [tipos, setTipos] = useState<TipoVariacao[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingSalvar, setLoadingSalvar] = useState(false);
  const [adicionandoTipo, setAdicionandoTipo] = useState(false);
  const [editandoTipo, setEditandoTipo] = useState<TipoVariacao | null>(null);
  const [tipoNome, setTipoNome] = useState("");

  const fetchTipos = async () => {
    setLoading(true);
    try {
      const response = await tiposVariacaoApi().getTipos();
      // id 4 = "Não definida": tipo de sistema, não pode ser editado pelo cliente.
      setTipos(response.filter((t: TipoVariacao) => t.id !== 4));
    } catch (error) {
      console.error("Erro ao buscar tipos de variação:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTipos();
      setAdicionandoTipo(false);
      setEditandoTipo(null);
      setTipoNome("");
    }
  }, [isOpen]);

  const handleToggleAtivo = async (tipo: TipoVariacao) => {
    try {
      const novoAtivo = tipo.ativo === 1 ? 0 : 1;
      await tiposVariacaoApi().atualizarTipo(tipo.id, { ativo: novoAtivo });
      // Re-busca: com copy-on-write um tipo global pode virar uma cópia da empresa (id novo).
      await fetchTipos();
    } catch (error) {
      console.error("Erro ao atualizar tipo:", error);
      Swal.fire({
        title: "Erro!",
        text: "Não foi possível atualizar o tipo de variação.",
        icon: "error",
        confirmButtonText: "Ok",
      });
    }
  };

  const handleSalvarTipo = async () => {
    if (tipoNome.trim() === "") {
      Swal.fire({
        title: "Erro!",
        text: "O nome do tipo não pode estar vazio.",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }

    if (tipoNome.length > 30) {
      Swal.fire({
        title: "Erro!",
        text: "O nome do tipo deve ter no máximo 30 caracteres.",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }

    setLoadingSalvar(true);

    try {
      if (editandoTipo) {
        await tiposVariacaoApi().atualizarTipo(editandoTipo.id, { nome: tipoNome.trim() });
      } else {
        await tiposVariacaoApi().criarTipo(tipoNome.trim());
      }
      // Re-busca: com copy-on-write a edição de um tipo global gera uma cópia da empresa (id novo).
      await fetchTipos();
      setAdicionandoTipo(false);
      setEditandoTipo(null);
      setTipoNome("");
    } catch (error) {
      console.error("Erro ao salvar tipo:", error);
      Swal.fire({
        title: "Erro!",
        text: editandoTipo ? "Não foi possível atualizar o tipo." : "Não foi possível criar o tipo.",
        icon: "error",
        confirmButtonText: "Ok",
      });
    } finally {
      setLoadingSalvar(false);
    }
  };

  const handleSave = () => {
    onSave?.(tipos);
    setModal(false);
  };

  const handleEditarClick = (tipo: TipoVariacao) => {
    setEditandoTipo(tipo);
    setTipoNome(tipo.nome);
    setAdicionandoTipo(true);
  };

  const handleNovoTipoClick = () => {
    setEditandoTipo(null);
    setTipoNome("");
    setAdicionandoTipo(true);
  };

  if (adicionandoTipo) {
    return (
      <Modal isOpen={isOpen} setModal={setModal} width="500px" maxWidth="90vw">
        <CriarTipoContainer theme={theme}>
          <div className="titulo fonteTitulo">
            {editandoTipo ? "Editar tipo de variação" : "Novo tipo de variação"}
          </div>
          <div className="texto fonteRotulos">
            {editandoTipo
              ? "Edite o nome do tipo de variação"
              : "Adicione um nome para identificar seu tipo de variação"
            }
          </div>
          <div className="inputs">
            <Input
              label="Nome do tipo"
              type="text"
              maxLength={30}
              name="nome"
              value={tipoNome}
              onChange={(newValue) => setTipoNome(newValue)}
              placeholder="Nome do tipo"
            />
          </div>
          <Botoes theme={theme}>
            <div
              className="cancelar fonteTexto"
              onClick={() => {
                setAdicionandoTipo(false);
                setEditandoTipo(null);
                setTipoNome("");
              }}
            >
              <Voltar /> Voltar
            </div>
            <Button
              title="Salvar"
              loading={loadingSalvar}
              className="botao_alterar fonteTexto"
              width="25rem"
              onClick={handleSalvarTipo}
              disabled={tipoNome.length === 0}
            />
          </Botoes>
        </CriarTipoContainer>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} setModal={setModal} width="600px">
      <ContainerModalTiposVariacao theme={theme}>
        <Titulo
          titulo="Tipos de variação"
          subtitulo="Configure os tipos de variação que deseja utilizar em seus produtos"
          flexDirection="column-reverse"
        />

        {loading ? (
          <div className="loading fonteTexto">Carregando tipos...</div>
        ) : (
          <>
            {tipos.length > 0 && (
              <Table
                columns={[
                  {
                    key: "nome",
                    label: "Nome",
                    mask: (value: string) => (
                      <span
                        className="fonteTexto nome-tipo"
                        onClick={() => {
                          const tipo = tipos.find(t => t.nome === value);
                          if (tipo) handleEditarClick(tipo);
                        }}
                      >
                        {value}
                      </span>
                    )
                  },
                  {
                    key: "situacao",
                    label: "Situação",
                    mask: (value: any) => value
                  }
                ]}
                data={tipos.map((tipo) => ({
                  ...tipo,
                  situacao: (
                    <div className="center">
                      <Toggle
                        value={tipo.ativo === 1}
                        onChange={() => handleToggleAtivo(tipo)}
                      />
                      <span
                        onClick={() => handleEditarClick(tipo)}
                        className="editar fonteTexto"
                      >
                        Editar
                      </span>
                    </div>
                  )
                }))}
                sortable={false}
                selectable={false}
                button={false}
                page={1}
                itensPorPagina={-1}
              />
            )}

            <div className="linha-pai">
              <div className="linha"></div>
            </div>
            <div className="novo-tipo fonteTexto">
              <span onClick={handleNovoTipoClick}>
                Adicionar novo tipo
              </span>
            </div>
          </>
        )}

        <Botoes theme={theme}>
          <div className="cancelar fonteTexto" onClick={() => setModal(false)}>
            <Voltar /> Voltar
          </div>
          <Button
            title="Salvar"
            onClick={handleSave}
            width="50%"
            className={`fonteTexto ${loading && "disabled"}`}
          />
        </Botoes>
      </ContainerModalTiposVariacao>
    </Modal>
  );
};
